import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { num, nomeMese, MESI_NOMI } from './calcoli';

// Righe apribili del business plan (03/11/2026, titolare: "una tendina che scende e giustifica da dove arrivano,
// dinamici, sia entrate che uscite; nomi non fuorvianti: entrate, uscite, positivo, negativo").
// spiega() costruisce le spiegazioni dai numeri del momento: cambiando un'ipotesi cambiano anche i conti mostrati.

export const VERDE = '#2F7D4F', ROSSO = '#B42318';
const e0 = (n) => `€ ${Math.round(n || 0).toLocaleString('it-IT')}`;
const e2 = (n) => `€ ${(Math.round((n || 0) * 100) / 100).toLocaleString('it-IT')}`;
const n2 = (n) => (Math.round((n || 0) * 100) / 100).toLocaleString('it-IT');

// Importo con segno e colore: + verde (entra), − rosso (esce). segno 'auto' = dal valore.
export const Importo = ({ v, segno = 'auto', grande }) => {
  const neg = segno === '-' || (segno === 'auto' && v < 0);
  const zero = !Math.round(v || 0);
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 700, fontSize: grande ? 'var(--font-size-xl)' : undefined, color: zero ? 'var(--text-muted)' : neg ? ROSSO : VERDE }}>
      {zero ? '' : neg ? '−' : '+'}{e0(Math.abs(v || 0))}
    </span>
  );
};

export function Dettaglio({ righe = [], fonte, segno }) {
  return (
    <div style={st.box}>
      {righe.map((x, i) => {
        const sg = x.segno ?? segno;
        return (
          <div key={i} style={{ ...st.riga, borderBottom: i < righe.length - 1 ? '1px dashed var(--border2)' : 'none', fontWeight: x.tot ? 700 : 400, ...(x.tot ? { borderTop: '1px solid var(--border2)' } : {}) }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ color: x.avviso ? ROSSO : 'var(--text)' }}>{x.l}</span>
              {x.f && <span style={{ display: 'block', color: 'var(--text-muted)', fontWeight: 400 }}>{x.f}</span>}
            </span>
            {x.v != null && <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{sg === '-' && Math.round(x.v) ? '−' : sg === '+' && Math.round(x.v) ? '+' : ''}{e0(x.v)}</span>}
          </div>
        );
      })}
      {fonte && <div style={{ color: 'var(--text-dim)', marginTop: '6px' }}><b>Da dove arrivano i dati:</b> {fonte}</div>}
    </div>
  );
}

// Riga di un conto: click → si apre il dettaglio sotto
export function Voce({ nome, nota, v, segno, righe, fonte, forte }) {
  const [aperto, setAperto] = useState(false);
  const apribile = (righe && righe.length > 0) || !!fonte;
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button type="button" onClick={() => apribile && setAperto(a => !a)} aria-expanded={aperto}
        style={{ ...st.voce, cursor: apribile ? 'pointer' : 'default', fontWeight: forte ? 800 : 400 }}>
        <span style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', textAlign: 'left', minWidth: 0 }}>
          {apribile ? (aperto ? <ChevronDown size={15} style={st.chev} /> : <ChevronRight size={15} style={st.chev} />) : <span style={{ width: 15, flexShrink: 0 }} />}
          <span>{nome}{nota && <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 400 }}>{nota}</span>}</span>
        </span>
        <Importo v={v} segno={segno} />
      </button>
      {aperto && <Dettaglio righe={righe} fonte={fonte} segno={segno} />}
    </div>
  );
}

// Tendina piccola sotto un riquadro
export function Tendina({ righe, fonte, segno, testo = 'Da dove arriva questo numero' }) {
  const [aperto, setAperto] = useState(false);
  return (
    <div style={{ marginTop: '6px' }}>
      <button type="button" onClick={() => setAperto(a => !a)} aria-expanded={aperto} style={st.link}>
        {aperto ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {testo}
      </button>
      {aperto && <Dettaglio righe={righe} fonte={fonte} segno={segno} />}
    </div>
  );
}

export const spiega = (srv, ip, r) => {
  const per = (p) => srv.periodi.find(x => x.periodo === p);
  const q1 = per('Q1'), q2 = per('Q2'), es = per('ESTIVO');
  const D = num(ip.durata_ore) || 1.5;
  const w1 = num(ip.periodi.Q1.settimane), w2 = num(ip.periodi.Q2.settimane), we = num(ip.periodi.ESTIVO.settimane);
  const fQ2 = num(ip.attivita_q2_pct), fEs = num(ip.estivo.attivita_pct);
  const R = r.ricavi, C = r.costi;
  const ciclo = (p) => (p ? `Planning, ciclo "${p.nome}"` : 'Planning');

  // --- ENTRATE ---
  const righeSedi = (p) => (p?.sedi || []).filter(s => s.quadrimestrali || s.annuali || s.annuali_precedenti || s.senza_pacchetto).map(s => {
    const parti = [];
    if (s.quadrimestrali) parti.push(`${s.quadrimestrali} quadrimestrali = ${e0(s.dovuto_quadrimestrali)}`);
    if (s.annuali) parti.push(`${s.annuali} annuali = ${e0(s.dovuto_annuali)}`);
    if (s.annuali_precedenti) parti.push(`${s.annuali_precedenti} con annuale già pagato prima = 0 €`);
    if (s.senza_pacchetto) parti.push(`${s.senza_pacchetto} senza pacchetto = 0 €`);
    return { l: s.nome, f: parti.join(' · '), v: s.dovuto_quadrimestrali + s.dovuto_annuali };
  });
  const entrateQ1 = {
    righe: [
      ...righeSedi(q1),
      { l: 'Quote di tesseramento', f: 'quote dei corsisti del ciclo (esonerati esclusi)', v: R.quoteQ1 },
      { l: 'Totale Q1', v: R.incassoQ1, tot: true },
      { l: `Già pagati ${e0(R.pagatoQ1)} · ancora da incassare ${e0(Math.max(0, R.incassoQ1 - R.pagatoQ1))}` },
    ],
    fonte: `${ciclo(q1)}: per ogni persona il pacchetto con i suoi sconti, come in Contabilità corsi. Gli annuali pagano tutto qui.`,
  };
  const nAnn = (q1?.sedi || []).reduce((a, s) => a + s.annuali, 0);
  const rinnovano = Math.round(R.nQuadr * num(ip.rinnovo_pct) / 100);
  const entrateQ2 = R.q2Reale
    ? { righe: [...righeSedi(q2), { l: 'Quote di tesseramento', v: q2.quote_tesseramento || 0 }, { l: 'Totale Q2', v: R.incassoQ2, tot: true }], fonte: `${ciclo(q2)} (il Q2 è già nel planning: numeri veri, non più stima)` }
    : {
      righe: [
        { l: 'Quadrimestrali del Q1', f: `${R.nQuadr} persone che a febbraio devono ripagare` },
        { l: 'Quanti rinnovano', f: `${R.nQuadr} × ${n2(num(ip.rinnovo_pct))}% = ${rinnovano}${num(ip.nuovi_q2) ? ` + ${num(ip.nuovi_q2)} nuovi = ${R.rinnovi}` : ''} iscrizioni` },
        { l: 'Prezzo medio di un quadrimestre', f: R.nQuadr ? `${e0(num(ip.prezzo_q2))} (i quadrimestrali del Q1 fanno ${e0(R.quadrQ1)} ÷ ${R.nQuadr} = ${e0(R.quadrQ1 / R.nQuadr)})` : e0(num(ip.prezzo_q2)) },
        { l: 'Annuali', f: `${nAnn} persone: hanno già pagato nel Q1, al Q2 portano 0 €` },
        { l: 'Totale Q2 (stima)', f: `${R.rinnovi} iscrizioni × ${e0(num(ip.prezzo_q2))}`, v: R.incassoQ2, tot: true },
      ],
      fonte: 'stima dalle IPOTESI (percentuale di rinnovo, nuovi iscritti, prezzo medio): le cambi nella scheda IPOTESI.',
    };
  const esReale = es && es.sedi.some(s => s.avviato);
  const entrateEs = !ip.estivo.attivo
    ? { righe: [{ l: 'Estate spenta nelle IPOTESI: conta 0 €' }] }
    : esReale
      ? { righe: [...righeSedi(es), { l: 'Totale estate', v: R.incassoEs, tot: true }], fonte: ciclo(es) }
      : { righe: [{ l: 'Stima', f: `${num(ip.estivo.iscritti)} iscritti × ${e0(num(ip.estivo.prezzo))}`, v: R.incassoEs, tot: true }], fonte: 'IPOTESI → estivo' };
  const altreEntrate = {
    righe: ip.entrate_extra.length
      ? [...ip.entrate_extra.map(x => ({ l: x.nome || '(senza nome)', f: x.mese ? nomeMese(x.mese) : null, v: num(x.importo) })), { l: 'Totale', v: R.extra, tot: true }]
      : [{ l: 'Nessuna entrata futura inserita (sponsor, contributi, eventi…)' }],
    fonte: 'scheda SPESE E ENTRATE, inserite a mano',
  };

  // --- USCITE ---
  const campi = [], allen = [];
  r.perSede.forEach(s => {
    const cfg = ip.sedi[s.nome] || {};
    const sv = (q1?.sedi || []).find(x => x.nome === s.nome) || {};
    if (cfg.modalita === 'CONTRATTO') {
      if (s.valSlot) {
        campi.push({ l: `${s.nome} · cifra fissa, usata dai corsi Q1`, f: `${s.gruppi} gruppi × ${w1} settimane × ${e2(s.valSlot)} a slot`, v: s.usoQ1 });
        campi.push({ l: `${s.nome} · cifra fissa, usata dai corsi Q2`, f: `${s.gruppi} gruppi × ${w2} settimane × ${e2(s.valSlot)} a slot`, v: s.usoQ2 });
        campi.push({ l: `${s.nome} · cifra fissa, campi liberi già pagati`, f: `${e0(num(cfg.contratto_annuo))} − usati dai corsi: Natale, estate, slot vuoti da sfruttare`, v: s.contrattoLibero });
      } else campi.push({ l: `${s.nome} · cifra fissa`, f: `contratto annuo ${e0(num(cfg.contratto_annuo))} (metà nel Q1, metà nel Q2)`, v: s.contratto });
    } else {
      const co = num(cfg.costo_ora);
      if (!co) campi.push({ l: `${s.nome}: manca il costo del campo a slot, conta 0 €`, avviso: true, v: 0 });
      else {
        campi.push({ l: `${s.nome} · Q1`, f: `${s.gruppi} gruppi × ${w1} settimane × ${e2(co)} a slot`, v: s.campiQ1 });
        campi.push({ l: `${s.nome} · Q2`, f: `${s.gruppi} gruppi × ${w2} settimane × ${e2(co)} a slot${fQ2 !== 100 ? ` × ${n2(fQ2)}% dei gruppi attivi` : ' (stessi gruppi del Q1)'}`, v: s.campiQ2 });
      }
    }
    const t = sv.tariffe_ora_settimana || 0;
    const aOre = s.gruppi - (sv.gruppi_allenatore_fisso || 0);
    const extra = [
      sv.gruppi_allenatore_fisso ? `${sv.gruppi_allenatore_fisso} gruppi con allenatore a compenso fisso: qui 0 €, sono nei compensi fissi` : '',
      sv.allenatori_non_trovati?.length ? `${sv.allenatori_non_trovati.join(', ')} senza scheda collaboratore: tariffa media ${e2(srv.tariffa_media)}/h` : '',
    ].filter(Boolean).join(' · ');
    if (aOre > 0 || extra) {
      const base = aOre > 0 ? `${aOre} gruppi a ore, tariffe sommate ${e2(t)} (media ${e2(t / aOre)}/h a gruppo) × ${n2(D)} h` : '';
      allen.push({ l: `${s.nome} · Q1`, f: [base && `${base} × ${w1} settimane`, extra].filter(Boolean).join(' · '), v: s.allQ1 });
      allen.push({ l: `${s.nome} · Q2`, f: base ? `${base} × ${w2} settimane${fQ2 !== 100 ? ` × ${n2(fQ2)}%` : ' (stessi gruppi del Q1)'}` : null, v: s.allQ2 });
    }
  });
  if (C.costiEs) {
    const t = `${we} settimane × ${n2(fEs)}% dei gruppi`;
    campi.push({ l: 'Estate · campi e allenatori insieme', f: t, v: C.costiEs });
  }
  const totCampi = C.campiQ1 + C.campiQ2 + C.contratti + C.costiEs;
  campi.push({ l: 'Totale campi', v: totCampi, tot: true });
  const totAll = C.allQ1 + C.allQ2;
  allen.push({ l: 'Totale allenatori a ore', v: totAll, tot: true });

  const fissi = (srv.collaboratori_fissi || []).map(c => ({
    l: c.nome,
    f: `${e0(num(c.mensile))} al mese × ${C.fissiMesi} mesi (da ${C.meseAvvioFissi ? nomeMese(C.meseAvvioFissi) : '—'})${c.allena ? ` · allena a ${Object.keys(c.gruppi_per_sede).join(', ')}` : ''}`,
    v: num(c.mensile) * C.fissiMesi,
  }));
  fissi.push({ l: 'Totale compensi fissi', f: `${e0(C.fissiMese)} al mese × ${C.fissiMesi} mesi`, v: C.fissi, tot: true });

  const altre = [
    ...ip.spese.map(x => ({ l: x.nome || '(senza nome)', f: x.frequenza === 'MENSILE' ? `${e0(num(x.importo))} al mese × 12` : `una volta l'anno, a ${MESI_NOMI[(num(x.mese) || 9) - 1]}`, v: num(x.importo) * (x.frequenza === 'MENSILE' ? 12 : 1) })),
    ...ip.future.map(x => ({ l: x.nome || '(senza nome)', f: `spesa programmata${x.mese ? `, ${nomeMese(x.mese)}` : ''}`, v: num(x.importo) })),
  ];
  if (!altre.length) altre.push({ l: 'Nessuna spesa inserita: commercialista, affiliazioni, assicurazioni, tessere, servizi… Finché è vuoto, l\'avanzo è più alto del vero.', avviso: true });
  else altre.push({ l: 'Totale', v: C.ricorrenti + C.future, tot: true });

  const usciteStagione = [
    { l: 'Campi', v: C.campiQ1 + C.campiQ2 + C.contratti },
    { l: 'Allenatori a ore', v: totAll },
    { l: 'Compensi fissi', v: C.fissi },
    { l: 'Altre spese (ricorrenti e programmate)', v: C.ricorrenti + C.future },
    { l: 'Totale uscite della stagione', v: C.costiAnno, tot: true },
  ];

  return {
    entrateQ1, entrateQ2, entrateEs, altreEntrate,
    campi: { righe: campi, fonte: 'Planning (quanti gruppi ha ogni sede) × Contratti strutture (costo a ora o cifra fissa) × IPOTESI (durata della lezione, settimane).' },
    allenatori: { righe: allen, fonte: 'Planning (allenatore di ogni gruppo) × tariffa oraria nella sua scheda collaboratore × IPOTESI (durata, settimane).' },
    fissi: { righe: fissi, fonte: 'schede dei collaboratori a compenso fisso; i mesi partono da quando inizia il Q1 (IPOTESI → mesi dei compensi fissi).' },
    altre: { righe: altre, fonte: 'scheda SPESE E ENTRATE, inserite a mano.' },
    usciteStagione: { righe: usciteStagione, fonte: 'Q1 + Q2, senza l\'estate (l\'estate è in più).' },
    riserva: {
      righe: [
        { l: 'Uscite della stagione', v: C.costiAnno },
        { l: `÷ 12 mesi × ${n2(num(ip.riserva_mesi))} mesi`, v: r.riserva, tot: true },
      ],
      fonte: 'IPOTESI → mesi di riserva. NON è una spesa: sono soldi da tenere in cassa per sicurezza (es. se i rinnovi vanno male).',
    },
    q2Minimo: {
      righe: [
        { l: 'Uscite della stagione', v: C.costiAnno, segno: '+' },
        { l: 'Riserva da mettere da parte', v: r.riserva, segno: '+' },
        { l: 'Entrate del Q1 (già nel planning)', v: R.incassoQ1, segno: '-' },
        ...(R.extra ? [{ l: 'Altre entrate', v: R.extra, segno: '-' }] : []),
        { l: 'Il Q2 deve portare almeno', v: r.servonoQ2, tot: true, segno: '' },
        ...(r.rinnoviNecessari != null ? [{ l: `÷ ${e0(num(ip.prezzo_q2))} a iscrizione = ${r.rinnoviNecessari} iscrizioni${r.pctNecessaria != null ? `, il ${r.pctNecessaria}% dei ${R.nQuadr} quadrimestrali` : ''}` }] : []),
      ],
      fonte: 'uscite e riserva come sopra; entrate Q1 dal planning.',
    },
  };
};

const st = {
  box: { margin: '0 0 10px 21px', padding: '8px 10px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: 'var(--font-size-xs)', lineHeight: 1.45 },
  riga: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', padding: '5px 0' },
  voce: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '7px 0', background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 'var(--font-size-sm)', fontFamily: 'inherit' },
  chev: { flexShrink: 0, marginTop: '2px', color: 'var(--text-dim)' },
  link: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', padding: 0, color: '#C24E0A', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};
